"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useMidiInputs } from "@/contexts/studio-context";
import { CloseIcon } from "@/components/ui/atoms/icons";
import { isOptedOut, setOptedOut } from "@/lib/analytics";
import { MidiSection } from "./midi-section";
import { MicSection } from "./mic-section";
import { KeyboardSection } from "./keyboard-section";
import { OutputSection } from "./output-section";
import { NotationSection } from "./notation-section";
import { AnimationsSection } from "./animations-section";
import { MiscSection } from "./misc-section";
import { PerformanceSection } from "./performance-section";
import { FONT_MONO, FONT_SERIF, SET } from "./palette";

/**
 * Mirror of the flag owned by `studio-page.tsx`. When set to `"1"`, the
 * welcome card (and the jingle) is skipped at boot. Default behavior is
 * "show every reload", so absence of the key means the card is enabled.
 */
const WELCOME_DISABLED_LS_KEY = "virtuosa.welcomeDisabled";

/** App version surfaced in the footer, refresh on each release. */
const APP_VERSION_LABEL = "VIRTUOSA · v0.4.2 · alpha";

const FADE_KEYFRAMES = `
@keyframes settings-overlay-fadein {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}
`;

interface SettingsOverlayProps {
  onClose: () => void;
}

/**
 * Studio Settings overlay, full-canvas surface that replaces the piano-roll
 * while the sidebar / minimap / piano keys stay framed around it. Mounted
 * inside `StudioFrame.overlay` (it is NOT a fixed window-level modal).
 *
 * Sibling of `PieceOverviewOverlay` in spirit: same aurora glow, same
 * sticky footer pattern, same `Escape` keyboard handler.
 */
export function SettingsOverlay({ onClose }: SettingsOverlayProps) {
  const { devices } = useMidiInputs();

  // ── ESC closes, same pattern as `PieceOverviewOverlay`. ──
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) {
        return;
      }

      const target = event.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();

      if (tag === "input" || tag === "textarea" || target?.isContentEditable) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Welcome card on/off, persisted in localStorage. Hydrated lazily so SSR
  // renders the default ("on") and the real value is read only on the
  // client. Local state mirrors the flag so the toggle reacts immediately
  // without a re-read; the change takes effect on the next page reload.
  const [welcomeEnabled, setWelcomeEnabled] = useState(true);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    setWelcomeEnabled(
      window.localStorage.getItem(WELCOME_DISABLED_LS_KEY) !== "1",
    );
  }, []);

  const handleToggleWelcome = useCallback((next: boolean) => {
    setWelcomeEnabled(next);

    if (typeof window === "undefined") {
      return;
    }

    if (next) {
      window.localStorage.removeItem(WELCOME_DISABLED_LS_KEY);

      return;
    }

    window.localStorage.setItem(WELCOME_DISABLED_LS_KEY, "1");
  }, []);

  // Opposition à la mesure d'usage. L'état affiché est l'inverse du drapeau
  // stocké : on ne pose une clé que lorsque l'utilisateur refuse, jamais pour
  // enregistrer un accord implicite.
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);

  /**
   * Le choix a-t-il seulement tenu en mémoire ?
   *
   * `setOptedOut` rend `false` quand le stockage a refusé l'écriture : Safari
   * en navigation privée, un navigateur qui bloque les données de site, un
   * quota plein. L'opposition est bien honorée, mais pour cette session
   * seulement. L'ignorer reviendrait à afficher un refus pris en compte pour
   * de bon alors qu'il s'évapore au rechargement, ce qui est pire que pas de
   * commutateur du tout.
   */
  const [analyticsChoiceKept, setAnalyticsChoiceKept] = useState(true);

  useEffect(() => {
    setAnalyticsEnabled(!isOptedOut());
  }, []);

  const handleToggleAnalytics = useCallback((next: boolean) => {
    setAnalyticsEnabled(next);
    setAnalyticsChoiceKept(setOptedOut(!next));
  }, []);

  const midiCountLabel = useMemo(() => {
    const active = devices.filter((d) => d.enabled && d.connected).length;

    return `${active}/${devices.length} actifs`;
  }, [devices]);

  return (
    <div
      role="dialog"
      aria-label="Paramètres du studio"
      style={{
        position: "relative",
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background: SET.bg,
      }}
    >
      <style>{FADE_KEYFRAMES}</style>

      {/* Aurora, soft radial glow that anchors the immersive direction.
          Same construction as PieceOverviewOverlay. */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background: `
            radial-gradient(ellipse 700px 360px at 50% 30%, rgba(139,92,246,0.13) 0%, transparent 60%),
            radial-gradient(ellipse 500px 260px at 30% 80%, rgba(103,232,249,0.05) 0%, transparent 70%)
          `,
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <div
        style={{
          position: "relative",
          zIndex: 1,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          animation: "settings-overlay-fadein 240ms cubic-bezier(0.2, 0.8, 0.2, 1)",
        }}
      >
        <Header onClose={onClose} />

        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            padding: "24px 40px 32px",
          }}
        >
          <div
            style={{
              maxWidth: 720,
              margin: "0 auto",
              display: "flex",
              flexDirection: "column",
              gap: 28,
            }}
          >
            <MidiSection countLabel={midiCountLabel} />
            <MicSection />
            <KeyboardSection />
            <OutputSection />
            <NotationSection />
            <AnimationsSection />
            <PerformanceSection />
            <MiscSection
              welcomeEnabled={welcomeEnabled}
              onToggleWelcome={handleToggleWelcome}
              analyticsEnabled={analyticsEnabled}
              analyticsChoiceKept={analyticsChoiceKept}
              onToggleAnalytics={handleToggleAnalytics}
              onClose={onClose}
            />
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Header, kicker · serif title · ESC chip + close button
// ──────────────────────────────────────────────────────────────

function Header({ onClose }: { onClose: () => void }) {
  return (
    <div
      style={{
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
        padding: "28px 40px 0",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <span
          style={{
            fontFamily: FONT_MONO,
            fontSize: 10,
            letterSpacing: "0.32em",
            color: SET.violetLight,
            textTransform: "uppercase",
            fontWeight: 500,
          }}
        >
          STUDIO · PARAMÈTRES
        </span>
        <h1
          style={{
            fontFamily: FONT_SERIF,
            fontWeight: 300,
            fontSize: 38,
            letterSpacing: "-0.02em",
            color: SET.text,
            lineHeight: 1.05,
            margin: 0,
          }}
        >
          Réglages
        </h1>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <kbd
          style={{
            padding: "4px 8px",
            borderRadius: 6,
            background: "rgba(255,255,255,0.04)",
            border: `1px solid ${SET.borderHi}`,
            color: SET.textDim,
            fontFamily: FONT_MONO,
            fontSize: 10,
            fontWeight: 500,
            letterSpacing: "0.06em",
          }}
        >
          ESC
        </kbd>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer les paramètres"
          style={{
            width: 32,
            height: 32,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: "transparent",
            border: `1px solid ${SET.border}`,
            borderRadius: 8,
            color: SET.textDim,
            cursor: "pointer",
            transition: "color 150ms, border-color 150ms, background 150ms",
            padding: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = SET.text;
            e.currentTarget.style.borderColor = SET.borderHi;
            e.currentTarget.style.background = SET.surface;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = SET.textDim;
            e.currentTarget.style.borderColor = SET.border;
            e.currentTarget.style.background = "transparent";
          }}
        >
          <CloseIcon size={14} />
        </button>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Footer, version · "modifications enregistrées automatiquement"
// ──────────────────────────────────────────────────────────────

function Footer() {
  return (
    <div
      style={{
        flexShrink: 0,
        height: 44,
        borderTop: `1px solid ${SET.border}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 40px",
        background: "rgba(7, 7, 9, 0.65)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
      }}
    >
      <span
        style={{
          fontFamily: FONT_MONO,
          fontSize: 10,
          letterSpacing: "0.18em",
          color: SET.textMute,
          textTransform: "uppercase",
        }}
      >
        {APP_VERSION_LABEL}
      </span>
      <span
        style={{
          fontSize: 11,
          color: SET.textMute,
          fontStyle: "italic",
        }}
      >
        Les modifications sont enregistrées automatiquement.
      </span>
    </div>
  );
}
